import type { CSSProperties, FC } from "react";
import {
  useEffect, useMemo, useRef, useState,
} from "react";
import type { Swiper as SwiperInstance } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import { InstallOnChrome } from "@/features/installOnChrome";
import type { IComponentBaseProps } from "@/shared/config";
import { useCN } from "@/shared/lib/hooks";
import { FeedbackCard } from "./FeedbackCard";
import leftArrow from "/assets/images/leftArrow.svg";
import rightArrow from "/assets/images/rightArrow.svg";
import star from "/assets/images/star.svg";

import type { IFeedback } from "../config";
import "./../style.pcss";
import "swiper/css";

interface IFeedbackProps extends IFeedback, IComponentBaseProps {}

/**
 * Компонент FeedbackSlider.
 * Реализует интерактивный слайдер с отзывами пользователей.
 * Поддерживает перелистывание, адаптивную верстку и свайпы на мобильных устройствах.
 * Все карточки автоматически выравниваются по высоте самой высокой карточки.
 *
 * @returns {ReactNode}
 */
export const FeedbackSlider: FC<IFeedbackProps> = ({
  heading,
  rating,
  subtitle,
  baseClass = "feedback",
  items,
}) => {
  const { getCN } = useCN(baseClass);

  const [ w, setW ] = useState<number>(
    typeof window !== "undefined" ? window.innerWidth : 1920
  );

  useEffect(() => {
    const onR = () => setW(window.innerWidth);
    window.addEventListener("resize", onR);
    return () => window.removeEventListener("resize", onR);
  }, []);

  const layout = useMemo(() => {
    if (w < 768) {
      return {
        visible: 1, cardWidth: 250, gap: 16, mobilePeek: true,
      };
    }
    if (w < 1267) {
      return {
        visible: 2, cardWidth: 340, gap: 20, mobilePeek: false,
      };
    }
    if (w < 1536) {
      return {
        visible: 2, cardWidth: 360, gap: 24, mobilePeek: false,
      };
    }
    return {
      visible: 3, cardWidth: 360, gap: 24, mobilePeek: false,
    };
  }, [ w ]);

  const showArrows = w >= 1000;
  const swiperRef = useRef<SwiperInstance | null>(null);

  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [ maxHeight, setMaxHeight ] = useState<number | null>(null);

  useEffect(() => {
    setMaxHeight(null);
    const frame = requestAnimationFrame(() => {
      const heights = cardRefs.current.map((el) => el?.offsetHeight ?? 0);
      const max = Math.max(...heights, 0);
      setMaxHeight(max || null);
    });

    return () => cancelAnimationFrame(frame);
  }, [ items, w ]);

  const computedSlidesPerView = useMemo(() => {
    if (layout.mobilePeek) {
      const availableWidth = Math.max(1, w - layout.gap);
      const raw = availableWidth / layout.cardWidth;
      return Math.min(items.length, Math.max(1, raw));
    }
    return layout.visible;
  }, [ items.length, layout, w ]);

  const loopSlidesThreshold = layout.mobilePeek
    ? Math.ceil(computedSlidesPerView)
    : layout.visible;
  const shouldLoop = items.length > loopSlidesThreshold;

  useEffect(() => {
    swiperRef.current?.update();
  }, [ layout, w, maxHeight, items.length, computedSlidesPerView, shouldLoop ]);

  const next = () => swiperRef.current?.slideNext();
  const prev = () => swiperRef.current?.slidePrev();
  const isTwoCardLayout = layout.visible === 2;
  const arrowOffset = useMemo(() => {
    if (w >= 1536) {
      return "-14rem";
    }
    if (w < 1170) {
      return "0rem";
    }
    if (isTwoCardLayout) {
      return "12.5rem";
    }
    return "1.5rem";
  }, [ isTwoCardLayout, w ]);

  const viewportStyle: CSSProperties =
    w < 768
      ? { width: "100%", paddingLeft: 0 }
      : { width: layout.visible * layout.cardWidth + (layout.visible - 1) * layout.gap };

  return (
    <section className={"w-full flex flex-col items-center relative gap-20 mobile:gap-12"}>
      <div className={"w-full flex flex-col items-center gap-4 max-w-200 mx-auto"}>
        <h2 className={getCN("title", {}, [ "text-center h2 max-w-200 w-full mb-10 mobile:mb-4" ])}>{heading}</h2>

        <div className={"flex items-center gap-2"}>
          <img draggable={false} src={star} alt={"rating"} className={"w-11 h-11 mobile:w-10 mobile:h-10"} />
          <p className={getCN("raiting", {}, [ "font-semibold text-center text-[15rem] tablet:text-[10rem] mobile:text-[8rem]" ])}>
            {rating}
          </p>
        </div>

        <p className={getCN("subtitle", {}, [ "text-center subtitle mb-3 mobile:mb-1" ])}>{subtitle}</p>

        <div className={"relative shadow-[0_8px_32px_rgba(255,119,162,0.45)] rounded-full mobile:w-full"}>
          <InstallOnChrome />
        </div>
      </div>

      <div className={"relative w-full max-w-375 mx-auto flex items-center justify-center"}>
        {showArrows && (
          <button
            onClick={prev}
            className={"absolute z-10 hidden tablet:flex desktop:flex items-center justify-center w-20 h-20 rounded-full bg-inherit hover:bg-gray-100 cursor-pointer transition"}
            style={{ left: arrowOffset }}
            aria-label={"Prev"}
          >
            <img draggable={false} src={leftArrow} alt={"leftArrow"} />
          </button>
        )}

        <div className={"overflow-hidden mobile:overflow-visible"} style={viewportStyle}>
          <Swiper
            className={getCN("swiper", {}, [ "feedback-swiper" ])}
            onSwiper={(instance) => {
              swiperRef.current = instance;
            }}
            slidesPerView={computedSlidesPerView}
            spaceBetween={layout.gap}
            loop={shouldLoop}
            watchSlidesProgress
          >
            {items.map((item, i) => {
              return (
                <SwiperSlide key={`${item.name}-${i}`}>
                  <div
                    ref={(el) => {
                      cardRefs.current[i] = el;
                    }}
                    style={{
                      height: maxHeight !== null ? `${maxHeight}px` : "auto",
                      transition: "height 0.3s ease",
                    }}
                  >
                    <FeedbackCard {...item} />
                  </div>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>

        {showArrows && (
          <button
            onClick={next}
            className={"absolute z-10 hidden tablet:flex desktop:flex items-center justify-center w-20 h-20 rounded-full bg-inherit hover:bg-gray-100 cursor-pointer transition"}
            style={{ right: arrowOffset }}
            aria-label={"Next"}
          >
            <img draggable={false} src={rightArrow} alt={"rightArrow"} />
          </button>
        )}
      </div>
    </section>
  );
};
